import { z } from "zod";

import { jurisdictionProfileSchema } from "../services/cableContract";
import { loadScenarioSchema } from "../services/loadDemandContract";
import {
  upsBatteryTechnologySchema,
  upsPhaseConfigurationSchema,
  upsRedundancyModeSchema,
  upsTopologySchema,
  type UPSBatteryTechnology,
  type UPSPhaseConfiguration,
  type UPSRedundancyMode,
  type UPSTopology,
} from "../services/upsSizingContract";
import { deriveStandbyUnits } from "../utils/sourceSizing";
import type { ValidationLabels } from "../utils/validationMessages";

// Draft model of the UPS sizing study form (EOS-03c): what the user is typing,
// kept apart from the components so the payload rules are tested without a
// browser.
//
// Every value is held as the string the user typed, including the module counts.
// A blank optional field is left OUT of the payload: under Master Prompt
// A16 (a) and A18 (f) a blank factor or efficiency means NOT ESTABLISHED, and
// the engine must see it as absent, never as "" and never as "1". The load
// power factor, the UPS rated output power factor and the required runtime are
// engineering inputs, not margins, so they stay required (A16 (b), A18 (b)).
//
// The redundancy mode has no default in the backend (A18 (h)), so the draft
// starts with none selected ("") and a blank choice is left out for the schema
// to report as required. Topology, phase configuration, battery technology and
// scenario are descriptors: optional, no default, echoed in the result, so a
// blank one is simply left out.
//
// The available module ratings are separate entries with stable ids, so adding
// or removing one never disturbs the others; the ids never reach the backend.
// Every entry is sent in the order it is on screen, blank ones included - the
// backend requires a unique ascending schedule of positive decimals and must be
// the one to say so, entry by entry.

/** The module key the draft is kept under in the browser tab (see useStudyDraft). */
export const UPS_STUDY_DRAFT_MODULE = "ups-sizing";

export type UPSRatingDraft = {
  id: string;
  value: string;
};

export type UPSStudyDraft = {
  code: string;
  name: string;
  criticalLoadKw: string;
  loadPowerFactor: string;
  upsRatedOutputPowerFactor: string;
  requiredRuntimeMinutes: string;
  inverterEfficiency: string;
  futureGrowthFactor: string;
  designMarginFactor: string;
  ambientDeratingFactor: string;
  altitudeDeratingFactor: string;
  dutyModules: string;
  // Derived from the redundancy mode and the duty modules; shown, never typed.
  redundantModules: string;
  redundancyMode: string;
  topology: string;
  phaseConfiguration: string;
  batteryTechnology: string;
  scenario: string;
  jurisdictionProfile: string;
  notes: string;
  /** The source picked from the revision's diagram, or "" (A28 (d)). */
  sldElementCode: string;
  unitRatings: UPSRatingDraft[];
};

// A choice the user has not made yet is held as "".
function chosenOrBlank<T extends z.ZodType<string>>(choice: T) {
  return z.union([z.literal(""), choice]);
}

// The shape a draft kept in the browser tab must still have to be restored: exactly the
// fields above, typed text as strings and the choices as values the contract knows or "".
// A draft saved by an older form fails here and is dropped instead of half-filling this one.
export const upsStudyDraftSchema = z
  .object({
    code: z.string(),
    name: z.string(),
    criticalLoadKw: z.string(),
    loadPowerFactor: z.string(),
    upsRatedOutputPowerFactor: z.string(),
    requiredRuntimeMinutes: z.string(),
    inverterEfficiency: z.string(),
    futureGrowthFactor: z.string(),
    designMarginFactor: z.string(),
    ambientDeratingFactor: z.string(),
    altitudeDeratingFactor: z.string(),
    dutyModules: z.string(),
    redundantModules: z.string(),
    redundancyMode: chosenOrBlank(upsRedundancyModeSchema),
    topology: chosenOrBlank(upsTopologySchema),
    phaseConfiguration: chosenOrBlank(upsPhaseConfigurationSchema),
    batteryTechnology: chosenOrBlank(upsBatteryTechnologySchema),
    scenario: chosenOrBlank(loadScenarioSchema),
    jurisdictionProfile: jurisdictionProfileSchema,
    notes: z.string(),
    // A draft kept before the field existed comes back as it was, with no source chosen.
    sldElementCode: z.string().default(""),
    unitRatings: z.array(z.object({ id: z.string(), value: z.string() }).strict()).min(1),
  })
  .strict();

let nextRatingNumber = 1;

// A prefix of its own, so a UPS rating id is never mistaken for a Transformer or DG one.
export function createUPSRatingId(): string {
  const id = `ups-rating-${nextRatingNumber}`;
  nextRatingNumber += 1;
  return id;
}

// Rating ids come from a counter that starts again at 1 on every page load, while a
// restored draft brings the ids it was saved with. Moving the counter past every id in use
// keeps an entry added afterwards from taking the id of one already on screen.
export function seedUPSRatingIdCounter(ids: readonly string[]): void {
  for (const id of ids) {
    const match = /^ups-rating-(\d+)$/.exec(id);
    if (match !== null) {
      nextRatingNumber = Math.max(nextRatingNumber, Number(match[1]) + 1);
    }
  }
}

export function createUPSRatingDraft(): UPSRatingDraft {
  return { id: createUPSRatingId(), value: "" };
}

// Only the defaults the backend itself has are filled in: one duty module, no
// redundant module and the IN profile. The redundancy mode and every descriptor
// start unselected.
export function createInitialUPSStudyDraft(): UPSStudyDraft {
  return {
    code: "",
    name: "",
    criticalLoadKw: "",
    loadPowerFactor: "",
    upsRatedOutputPowerFactor: "",
    requiredRuntimeMinutes: "",
    inverterEfficiency: "",
    futureGrowthFactor: "",
    designMarginFactor: "",
    ambientDeratingFactor: "",
    altitudeDeratingFactor: "",
    dutyModules: "1",
    redundantModules: "0",
    redundancyMode: "",
    topology: "",
    phaseConfiguration: "",
    batteryTechnology: "",
    scenario: "",
    jurisdictionProfile: "IN",
    notes: "",
    sldElementCode: "",
    unitRatings: [createUPSRatingDraft()],
  };
}

// An optional text or decimal field: sent trimmed, or left out when blank.
function optional(key: string, typed: string): Record<string, string> {
  const value = typed.trim();
  return value === "" ? {} : { [key]: value };
}

// A module count is a whole number on the contract. A clean whole number is sent
// as one; anything else is passed through as typed, so the schema reports it
// instead of this function guessing what the engineer meant.
function countValue(typed: string): unknown {
  const value = typed.trim();
  return /^\d+$/.test(value) ? Number(value) : typed;
}

// The request body as the form will send it. It is validated afterwards with
// upsRunCreateRequestSchema, so this function does not judge the values and
// never reorders the rating schedule. The redundant count follows the shared
// EOS-03 redundancy rule (the backend schemas agree).
export function buildUPSRunPayload(
  draft: UPSStudyDraft,
  projectRevisionId?: string,
): Record<string, unknown> {
  return {
    study: {
      code: draft.code.trim(),
      name: draft.name.trim(),
      critical_load_kw: draft.criticalLoadKw.trim(),
      load_power_factor: draft.loadPowerFactor.trim(),
      ups_rated_output_power_factor: draft.upsRatedOutputPowerFactor.trim(),
      required_runtime_minutes: draft.requiredRuntimeMinutes.trim(),
      // Every entry is sent, blank ones as "", so the index in a message such as
      // "Module rating 2 is required." always matches the entry's position on
      // screen. The schema refuses the blank; this builder does not hide it.
      available_unit_ratings_kva: draft.unitRatings.map((rating) => rating.value.trim()),
      // A16 (a), A18 (f): blank = not established (left out), never "1".
      ...optional("inverter_efficiency", draft.inverterEfficiency),
      ...optional("future_growth_factor", draft.futureGrowthFactor),
      ...optional("design_margin_factor", draft.designMarginFactor),
      ...optional("ambient_derating_factor", draft.ambientDeratingFactor),
      ...optional("altitude_derating_factor", draft.altitudeDeratingFactor),
      duty_modules: countValue(draft.dutyModules),
      redundant_modules: countValue(deriveStandbyUnits(draft.redundancyMode, draft.dutyModules)),
      ...optional("redundancy_mode", draft.redundancyMode),
      // Descriptors (A18 (h)): left out when not declared.
      ...optional("topology", draft.topology),
      ...optional("phase_configuration", draft.phaseConfiguration),
      ...optional("battery_technology", draft.batteryTechnology),
      ...optional("scenario", draft.scenario),
      ...optional("jurisdiction_profile", draft.jurisdictionProfile),
      ...optional("sld_element_code", draft.sldElementCode),
    },
    ...(projectRevisionId === undefined ? {} : { project_revision_id: projectRevisionId }),
    ...optional("notes", draft.notes),
  };
}

// On-screen labels of the UPS study form, keyed by the request path.
// The run request nests the study under "study", so every study path carries
// that prefix; describeLocation numbers the schedule entries, giving
// "Module rating 2 - ...". Shared factors carry the Transformer form's wording.
export const UPS_STUDY_LABELS: ValidationLabels = {
  fields: {
    "study.code": "Study code",
    "study.name": "Study name",
    "study.sld_element_code": "Source from the diagram",
    "study.critical_load_kw": "Critical load (kW)",
    "study.load_power_factor": "Load power factor",
    "study.ups_rated_output_power_factor": "UPS rated output power factor",
    "study.required_runtime_minutes": "Required runtime (min)",
    "study.available_unit_ratings_kva": "Module ratings",
    "study.inverter_efficiency": "Inverter efficiency",
    "study.future_growth_factor": "Future growth factor",
    "study.design_margin_factor": "Design margin factor",
    "study.ambient_derating_factor": "Ambient derating factor",
    "study.altitude_derating_factor": "Altitude derating factor",
    "study.duty_modules": "Duty modules",
    "study.redundant_modules": "Redundant modules",
    "study.redundancy_mode": "Redundancy mode",
    "study.topology": "Topology",
    "study.phase_configuration": "Phase configuration",
    "study.battery_technology": "Battery technology",
    "study.scenario": "Scenario",
    "study.jurisdiction_profile": "Jurisdiction profile",
    notes: "Study notes",
  },
  // One entry of the rating schedule: "Module rating 2".
  groups: { available_unit_ratings_kva: "Module rating" },
};

// The words of every choice the form offers, typed against the contract enums
// so a value the contract gains cannot pass without a label. The redundancy
// wording follows the Transformer and DG forms, with "module" in place of "unit".
// The scenario words are the load module's (SCENARIO_LABELS in LoadRow).

export const UPS_REDUNDANCY_LABELS: Record<UPSRedundancyMode, string> = {
  NONE: "None (no redundant module)",
  N_PLUS_1: "N+1 (one redundant module)",
  TWO_N: "2N (a redundant module for every duty module)",
};

export const UPS_REDUNDANCY_HINTS: Record<UPSRedundancyMode, string> = {
  NONE: "None: no redundant module.",
  N_PLUS_1: "N+1: one redundant module.",
  TWO_N: "2N: equal to duty modules.",
};

export const UPS_TOPOLOGY_LABELS: Record<UPSTopology, string> = {
  OFFLINE: "Offline (passive standby)",
  LINE_INTERACTIVE: "Line-interactive",
  ONLINE_DOUBLE_CONVERSION: "Online double conversion",
};

export const UPS_PHASE_CONFIGURATION_LABELS: Record<UPSPhaseConfiguration, string> = {
  SINGLE_PHASE: "Single-phase",
  THREE_PHASE: "Three-phase",
};

export const UPS_BATTERY_TECHNOLOGY_LABELS: Record<UPSBatteryTechnology, string> = {
  VRLA: "VRLA (valve-regulated lead-acid)",
  LITHIUM_ION: "Lithium-ion",
  NICKEL_CADMIUM: "Nickel-cadmium",
};
